'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';
import { tw } from '@tw';
import type {
  SubscriptionDto,
  SubscriptionPlanDto,
} from '@peach/types/subscriptions/subscription.dto.js';
import { PAUSE_WEEKS_OPTIONS } from '@peach/types/subscriptions/types.js';
import {
  bookSubscriptionCall,
  cancelSubscription,
  changeSubscriptionPlan,
  pauseSubscription,
} from '@/app/actions/subscriptions';
import { callAction } from '@/lib/call-action';
import { useTimezone } from '@/lib/hooks/useTimezone';
import { useClock } from '@/lib/hooks/useClock';
import { useCalendarStore } from '@/lib/stores/calendar.store';
import { formatDate, formatDateInTz } from '@/lib/utils/date';
import { SlotPicker } from '../SlotPicker';
import { planName, planPrice } from './plan-copy';

interface SubscriptionCardProps {
  subscription: SubscriptionDto;
  /** The creator's live plans, to move to. */
  plans: SubscriptionPlanDto[];
  tutorUsername: string;
  tutorName: string;
  serverNow: number;
}

/** A subscriber's whole subscription in one card: their calls, the calendar, and the plan. */
export function SubscriptionCard({
  subscription: sub,
  plans,
  tutorUsername,
  tutorName,
  serverNow,
}: SubscriptionCardProps) {
  const router = useRouter();
  const timezone = useTimezone();
  const now = useClock(serverNow);
  const calendarChanged = useCalendarStore((s) => s.changed);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [managing, setManaging] = useState(false);

  const membership = sub.plan.callsPerWeek === 0;
  const pausedNow =
    !!sub.pausedFrom && !!sub.pausedUntil && new Date(sub.pausedUntil).getTime() > now;
  const canBook = membership ? sub.welcomeCallOpen : true;

  const act = useCallback(
    async (run: () => Promise<{ success: boolean; error?: string }>) => {
      setBusy(true);
      setError(null);
      const result = await callAction(run);
      setBusy(false);
      if (!result.success) setError(result.error ?? 'Something went wrong, try again');
      else router.refresh();
    },
    [router]
  );

  const book = useCallback(
    async (isoTime: string, tz: string) => {
      const result = await callAction(() =>
        bookSubscriptionCall(sub.id, isoTime, tz, tutorUsername)
      );
      if (!result.success) return result.error;
      calendarChanged(tutorUsername);
      router.refresh();
    },
    [calendarChanged, router, sub.id, tutorUsername]
  );

  const status = sub.cancelAt
    ? `Ends ${formatDate(sub.cancelAt, 'MMM D')}`
    : pausedNow
      ? `Paused until ${formatDate(sub.pausedUntil!, 'MMM D')}`
      : `Renews ${formatDate(sub.paidThrough, 'MMM D')}`;
  const allowance = membership
    ? sub.welcomeCallOpen
      ? 'Your welcome call is ready to book'
      : 'Posts, and your welcome call is booked or done'
    : `${sub.callsLeftThisWeek} of ${sub.plan.callsPerWeek} ${
        sub.plan.callsPerWeek === 1 ? 'call' : 'calls'
      } left this week`;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-head-3 font-extrabold text-[var(--sf-ink)]">
          {planName(sub.plan.callsPerWeek)}
        </span>
        <span className="sf-note shrink-0">{status}</span>
      </div>
      <span className="sf-note">{allowance}</span>

      {sub.calls.length > 0 && (
        <ul className="flex flex-col gap-1.5">
          {sub.calls.map((call) => (
            <li
              key={call.id}
              className="flex items-center justify-between gap-3 rounded-[10px] bg-[var(--sf-page)] px-3 py-2"
            >
              {/* The hour waits for the viewer's zone, as on every call card. */}
              <span className="text-p-3 text-[var(--sf-ink)]" suppressHydrationWarning>
                {timezone ? formatDateInTz(call.start, 'ddd, MMM D [at] HH:mm', timezone) : '…'}
              </span>
              <a href={`/call/${call.id}`} className="mm-btn sf-cta sf-cta-line !w-auto px-3">
                Join
              </a>
            </li>
          ))}
        </ul>
      )}

      {canBook && (
        <SlotPicker
          tutorUsername={tutorUsername}
          calendar={{ courseId: sub.courseId }}
          empty={`No times open right now, ${tutorName} will open more`}
          serverNow={serverNow}
          busyLabel="Booking…"
          label={(time) =>
            time
              ? `Book ${time.format('ddd, MMM D')} at ${time.format('HH:mm')}`
              : 'Pick a day and time'
          }
          onPick={book}
        />
      )}

      {error && <span className="sf-note text-error">{error}</span>}

      <button
        type="button"
        onClick={() => setManaging((m) => !m)}
        className="mm-btn sf-cta sf-cta-line"
        aria-expanded={managing}
      >
        {managing ? 'Done' : 'Manage subscription'}
      </button>

      {managing && (
        <div className="flex flex-col gap-2">
          {plans
            .filter((plan) => plan.id !== sub.plan.id)
            .map((plan) => (
              <button
                key={plan.id}
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(() => changeSubscriptionPlan(sub.id, plan.id, tutorUsername))
                }
                className="mm-btn sf-cta sf-cta-line"
              >
                Switch to {planName(plan.callsPerWeek).toLowerCase()}, {planPrice(plan)}
              </button>
            ))}
          {plans.length > 1 && (
            <span className="sf-note">
              A bigger plan starts now and you pay the difference. A smaller one is credited to your
              next payment.
            </span>
          )}

          {!sub.cancelAt && !pausedNow && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="sf-note">Pause from next week for</span>
              {PAUSE_WEEKS_OPTIONS.map((weeks) => (
                <button
                  key={weeks}
                  type="button"
                  disabled={busy}
                  onClick={() => void act(() => pauseSubscription(sub.id, weeks, tutorUsername))}
                  className={tw('mm-btn rounded-full border border-[var(--sf-line)] px-3 py-1')}
                >
                  <span className="sf-num text-p-3 text-[var(--sf-ink)]">
                    {weeks} {weeks === 1 ? 'week' : 'weeks'}
                  </span>
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            disabled={busy}
            onClick={() => void act(() => cancelSubscription(sub.id, !sub.cancelAt, tutorUsername))}
            className="mm-btn sf-cta sf-cta-line"
          >
            {sub.cancelAt ? 'Keep my subscription' : 'Cancel at the end of the paid weeks'}
          </button>
        </div>
      )}
    </div>
  );
}
